import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { Editor, useEditorState } from '@tiptap/react';
import { FC, lazy, ReactNode, Suspense, useState } from 'react';
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdLink,
  MdLinkOff,
  MdOutlineEmojiEmotions,
} from 'react-icons/md';

// Lazy-load the emoji picker panel so it doesn't bloat the editor bundle.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

interface ToolbarProps {
  editor: Editor | null;
  /** Extra action buttons appended after the built-in tools (e.g. the post composer's attach/poll). */
  extraActions?: ReactNode;
}

export const RichTextToolbar: FC<ToolbarProps> = ({ editor, extraActions }) => {
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');

  const editorState = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) {
        return {
          isBoldActive: false,
          isItalicActive: false,
          isLinkActive: false,
          isBulletListActive: false,
          canBold: false,
          canItalic: false,
          canBulletList: false,
        };
      }
      return {
        isBoldActive: ed.isActive('bold'),
        isItalicActive: ed.isActive('italic'),
        isLinkActive: ed.isActive('link'),
        isBulletListActive: ed.isActive('bulletList'),
        canBold: ed.can().chain().toggleBold().run(),
        canItalic: ed.can().chain().toggleItalic().run(),
        canBulletList: ed.can().chain().toggleBulletList().run(),
      };
    },
  });

  if (!editor || !editorState) {
    return null;
  }

  const openLinkPopover = (target: HTMLElement) => {
    setLinkUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkAnchor(target);
  };

  const closeLinkPopover = () => setLinkAnchor(null);

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      removeLink();
      return;
    }

    const { from, to } = editor.state.selection;
    const isCollapsed = from === to;
    if (isCollapsed && !editor.isActive('link')) {
      // No text selected and not editing an existing link: `setLink` would have
      // nothing to mark, so insert the URL itself as the linked text.
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    closeLinkPopover();
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLinkPopover();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Tooltip title="Bold">
        <span>
          <IconButton
            size="small"
            onMouseDown={e => {
              e.preventDefault();
              editor.chain().focus().toggleBold().run();
            }}
            disabled={!editorState.canBold}
            color={editorState.isBoldActive ? 'primary' : 'default'}
            aria-label="Bold"
          >
            <MdFormatBold size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Italic">
        <span>
          <IconButton
            size="small"
            onMouseDown={e => {
              e.preventDefault();
              editor.chain().focus().toggleItalic().run();
            }}
            disabled={!editorState.canItalic}
            color={editorState.isItalicActive ? 'primary' : 'default'}
            aria-label="Italic"
          >
            <MdFormatItalic size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Bullet list">
        <span>
          <IconButton
            size="small"
            onMouseDown={e => {
              e.preventDefault();
              editor.chain().focus().toggleBulletList().run();
            }}
            disabled={!editorState.canBulletList}
            color={editorState.isBulletListActive ? 'primary' : 'default'}
            aria-label="Bullet list"
          >
            <MdFormatListBulleted size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Link">
        <span>
          <IconButton
            size="small"
            onMouseDown={e => {
              e.preventDefault();
              openLinkPopover(e.currentTarget);
            }}
            color={editorState.isLinkActive ? 'primary' : 'default'}
            aria-label="Link"
          >
            <MdLink size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Emoji">
        <span>
          <IconButton
            size="small"
            onMouseDown={e => {
              e.preventDefault();
              setEmojiAnchor(e.currentTarget);
            }}
            color="default"
            aria-label="Emoji"
          >
            <MdOutlineEmojiEmotions size={18} />
          </IconButton>
        </span>
      </Tooltip>
      {extraActions}

      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={closeLinkPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', gap: 1, p: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            autoFocus
            placeholder="https://example.com"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
            }}
            sx={{ minWidth: 240 }}
          />
          <Button size="small" variant="contained" onClick={applyLink} disabled={!linkUrl.trim()}>
            Apply
          </Button>
          {editorState.isLinkActive && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={removeLink}
              startIcon={<MdLinkOff size={16} />}
            >
              Remove
            </Button>
          )}
        </Box>
      </Popover>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, width: 300 }}>
              <CircularProgress size={20} />
            </Box>
          }
        >
          <EmojiPicker
            onEmojiClick={emoji => {
              editor.chain().focus().insertContent(emoji.emoji).run();
              setEmojiAnchor(null);
            }}
          />
        </Suspense>
      </Popover>
    </Box>
  );
};
